import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { useListDiscounts } from 'src/features/discounts/api/useListDiscounts';
import { discountsMessages } from 'src/features/discounts/utils/messages';
import { Discount } from 'src/features/discounts/utils/types';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { useParams } from 'react-router-dom';
import CreateDiscountFormModal from 'src/features/discounts/components/forms/CreateDiscountFormModal';
import CellDiscountActions from 'src/features/discounts/components/tables/CellDiscountActions';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const DiscountsTable = () => {
    const { formatMessage } = useIntl();

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'start_date', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const permissionManageDiscounts = useHasPermissions([PERMISSION.MANAGE_DISCOUNTS]);
    const permissionAccessSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);
    const permissionAccessDiscounts = useHasPermissions([PERMISSION.ACCESS_DISCOUNTS]);

    const { subscriptionId } = useParams();

    const {
        data: discounts,
        isLoading,
        isError,
    } = useListDiscounts({
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...(subscriptionId && { subscription: subscriptionId }),
    });

    const columnHelper = createColumnHelper<Discount>();

    const columns = [
        columnHelper.accessor('start_date', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            // A discount has no name, so the start date doubles as the row title that opens its dashboard.
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/discounts/${ctx.row.original.id}`}
                    render={() => <CellDatetime {...ctx} />}
                />
            ),
            meta: {
                title: formatMessage(discountsMessages.startDate),
            },
        }),
        columnHelper.accessor('end_date', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(discountsMessages.endDate),
                inputType: INPUT_TYPE.DATE,
            },
        }),
        columnHelper.accessor('discount_policy.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const discountPolicyId = ctx.row.original.discount_policy.id;
                return (
                    <CellLinkWithIcon
                        to={`/discount-policies/${discountPolicyId}`}
                        {...ctx}
                        hasPermissions={permissionAccessDiscounts}
                    />
                );
            },
            meta: {
                title: formatMessage(discountPoliciesMessages.discountPolicy),
            },
        }),
        columnHelper.accessor('subscription.service_plan.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const subscriptionId = ctx.row.original.subscription.id;
                const subscription = ctx.row.original.subscription;
                const subscriptionServicePlan = subscription.service_plan;
                const subscriptionDisplay = `${subscription.reference} - ${subscriptionServicePlan.name}`;

                return (
                    <CellLinkWithIcon
                        to={`/subscriptions/${subscriptionId}`}
                        {...ctx}
                        render={() => subscriptionDisplay}
                        hasPermissions={permissionAccessSubscriptions}
                    />
                );
            },
            meta: {
                title: formatMessage(subscriptionsMessages.subscription),
            },
        }),
        columnHelper.accessor('subscription_item.service_plan_item.name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const subscriptionId = ctx.row.original.subscription.id;
                const subscriptionItemId = ctx.row.original.subscription_item?.id;
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/subscriptions/${subscriptionId}/subscription-item/${subscriptionItemId}`}
                        hasPermissions={permissionAccessSubscriptions}
                    />
                );
            },
            meta: {
                title: formatMessage(subscriptionItemsMessages.subscriptionItems),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellDiscountActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<Discount, unknown>
            columns={columns}
            data={discounts}
            semantics={{
                title: formatMessage(discountPoliciesMessages.discounts),
                entryName: formatMessage(discountsMessages.discount).toLowerCase(),
                entryNames: formatMessage(discountsMessages.discountsBadge).toLowerCase(),
                placeholder: formatMessage(discountsMessages.discount).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageDiscounts && { newEntry: CreateDiscountFormModal }),
            }}
        />
    );
};

export default DiscountsTable;
